import React, { memo, isValidElement } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import Typography from '@mui/material/Typography'

import useReplacedText from '@v4/hooks/useReplacedText'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import AppStore from '@v4/store/appStore'
import useEditor, { EDITOR_VARIANT } from '@v4/hooks/useEditor'
import useCategoryScoreMergeTags from '@v4/hooks/useCategoryScoreMergeTags'
import useLeadAnswerMergeTags from '@v4/hooks/useLeadAnswerMergeTags'
import useQuizMergeTags from '@v4/hooks/useQuizMergeTags'
import useStyles from './EditableHTML.styled'

const fallbackFn = () => {}

const EditableHTML = props => {
	const {
		className,
		usePaperColors,
		extraShortcuts,
		editorVariant,
		placeholder,
		editorValidElements,
		html = '',
		autoFocus,
		displayEmpty = false,
		white = false,
		shadow = false,
		editable = false,
		textShadow = 40,
		onChange = fallbackFn,
		onFocus = fallbackFn,
		onBlur = fallbackFn,
		listIcon = 'fa-check',
		...rest
	} = props
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()

	const contentEditable = editable && isBuilderMode
	const pageType = PageBuilderSelector.useType()
	const classes = useStyles({ ...props, listIcon, pageType, displayEmpty, white, shadow, editable, textShadow })
	const categoryScoreMergeTags = useCategoryScoreMergeTags()
	const leadAnswerMergeTags = useLeadAnswerMergeTags()
	const quizMergeTags = useQuizMergeTags()
	const replacedHTML = useReplacedText(html, {
		extraShortcuts,
		scoreShortcuts: categoryScoreMergeTags?.shortcuts,
		leadAnswerShortcuts: leadAnswerMergeTags?.shortcuts,
		quizShortcuts: quizMergeTags?.shortcuts,
	})

	const { active, elementRef } = useEditor({
		active: contentEditable,
		value: html ?? '',
		parsedValue: replacedHTML ?? '',
		extraShortcuts,
		onChange,
		onFocus,
		onBlur,
		variant: editorVariant,
		allowEmpty: displayEmpty,
		clickOnFocus: isBuilderMode && !isQuestionBuilderMode,
		validElements: editorValidElements,
		categoryScoreMergeTags,
		leadAnswerMergeTags,
		quizMergeTags,
		autoFocus,
	})

	if ((!displayEmpty || isValidElement(displayEmpty)) && !html) return displayEmpty || null

	return (
		<Typography
			ref={elementRef}
			key={contentEditable ? 'editable' : 'not-editable'}
			className={clsx(className, classes.root, contentEditable && classes.editable, 'text-block')}
			data-placeholder={placeholder}
			{...rest}
			{...(active && contentEditable
				? { tabIndex: -1 }
				: { onFocus, onBlur, dangerouslySetInnerHTML: { __html: replacedHTML } })}
		/>
	)
}

EditableHTML.propTypes = {
	className: PropTypes.string,
	usePaperColors: PropTypes.bool,
	white: PropTypes.oneOf([false, true, 'theme']),
	shadow: PropTypes.bool,
	html: PropTypes.string,
	displayEmpty: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
	align: PropTypes.oneOf(['center', 'inherit', 'left', 'justify', 'right']),
	variant: PropTypes.oneOf([
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'subtitle1',
		'subtitle2',
		'body1',
		'body2',
		'caption',
		'button',
		'overline',
		'srOnly',
		'inherit',
	]),
	component: PropTypes.elementType,
	extraShortcuts: PropTypes.objectOf(PropTypes.string),
	onClick: PropTypes.func,
	editable: PropTypes.bool,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	editorVariant: PropTypes.oneOf(Object.values(EDITOR_VARIANT)),
	placeholder: PropTypes.string,
	editorValidElements: PropTypes.string,
	textShadow: PropTypes.number,
	autoFocus: PropTypes.bool,
	listIcon: PropTypes.string,
}

export default memo(EditableHTML)
